// Catálogo de plantillas .docx del sistema. Para añadir un documento nuevo,
// registra aquí su plantilla (archivo en /public/assets/plantillas) y sus
// marcadores; luego escribe su armador de datos (ver acta-reparto.ts).
//
// Convención de marcadores en el .docx (delimitadores { }):
//   · Valor simple:  {CLAVE}
//   · Tabla/lista:   fila con {#lista} … {CLAVE_FILA} … {/lista}
//     (el {#lista} y el {/lista} deben ir en la MISMA fila o abarcarla completa).

export interface DefinicionPlantilla {
  id: string;
  nombre: string; // etiqueta visible en la UI
  ruta: string; // ruta pública de la plantilla (servida desde /public)
  archivoBase: string; // nombre de archivo esperado en assets
  marcadores: string[]; // documentación de los { } que usa la plantilla
}

// Carpeta pública donde se dejan las plantillas .docx.
export const DIR_PLANTILLAS = '/assets/plantillas';

export const PLANTILLAS = {
  ACTA_REPARTO: {
    id: 'ACTA_REPARTO',
    nombre: 'Acta de reparto',
    archivoBase: 'ACTA-DE-REPARTO.docx',
    ruta: `${DIR_PLANTILLAS}/ACTA-DE-REPARTO.docx`,
    marcadores: [
      'ENCABEZADO', 'LUGAR', 'FECHA_LETRAS', 'RADICADO',
      'DIRECTOR', 'ABOGADO', 'TECNICO',
      // Tabla ASUNTOS A REPARTIR (bucle): {#ASUNTOS} … {/ASUNTOS}
      'ASUNTOS[].RADICADO', 'ASUNTOS[].IMPLICADO', 'ASUNTOS[].TRAMITE', 'ASUNTOS[].FOLIOS',
    ],
  },
  AUTO_INHIBITORIO: {
    id: 'AUTO_INHIBITORIO',
    nombre: 'Auto inhibitorio (Art. 209)',
    archivoBase: 'AUTO-INHIBITORIO.docx',
    ruta: `${DIR_PLANTILLAS}/AUTO-INHIBITORIO.docx`,
    marcadores: [
      // Tabla de cabecera
      'RADICADO', 'IMPLICADO', 'CARGO_ENTIDAD', 'QUEJOSO', 'FECHA_QUEJA', 'FECHA_HECHOS',
      // Lugar / fecha del auto
      'CIUDAD', 'FECHA_LETRAS',
      // Cuerpo editable
      'FOLIOS', 'ANTECEDENTES', 'MOTIVACION', 'CONCLUSION',
      // Firmas
      'DIRECTOR', 'ELABORO',
    ],
  },
  // Documento de la decisión "Indagación previa (Art. 208)" — usado cuando la
  // decisión de la indagación es "Archivo definitivo (Art. 224)".
  // Marcadores actuales: cabecera + fecha (el mapeo fino del cuerpo va después).
  INDAGACION_PREVIA: {
    id: 'INDAGACION_PREVIA',
    nombre: 'Auto de indagación previa (Art. 208)',
    archivoBase: 'INDAGACION-PREVIA.docx',
    ruta: `${DIR_PLANTILLAS}/INDAGACION-PREVIA.docx`,
    marcadores: ['RADICADO', 'INVESTIGADO', 'CARGO_ENTIDAD', 'FECHA_QUEJA', 'CIUDAD', 'FECHA_LETRAS'],
  },
  // Documento "Auto de apertura de investigación disciplinaria (Art. 211-213)".
  AUTO_APERTURA: {
    id: 'AUTO_APERTURA',
    nombre: 'Auto de apertura de investigación (Art. 211-213)',
    archivoBase: 'AUTO-APERTURA-INVESTIGACION.docx',
    ruta: `${DIR_PLANTILLAS}/AUTO-APERTURA-INVESTIGACION.docx`,
    marcadores: ['RADICADO', 'INVESTIGADO', 'CARGO_ENTIDAD', 'CIUDAD', 'FECHA_LETRAS'],
  },
  // "Auto que prorroga el término de la investigación disciplinaria (Art. 213)".
  // FECHA_LETRAS = fecha del auto (desde la que corre la 2ª prórroga de 3 meses).
  AUTO_PRORROGA: {
    id: 'AUTO_PRORROGA',
    nombre: 'Auto que prorroga la investigación (Art. 213)',
    archivoBase: 'AUTO-PRORROGA-INVESTIGACION.docx',
    ruta: `${DIR_PLANTILLAS}/AUTO-PRORROGA-INVESTIGACION.docx`,
    marcadores: ['RADICADO', 'INVESTIGADO', 'CARGO_ENTIDAD', 'FECHA_QUEJA', 'CIUDAD', 'FECHA_LETRAS'],
  },
  // "Versión libre (numeral 3, artículo 112 de la Ley 1952 de 2019)".
  VERSION_LIBRE: {
    id: 'VERSION_LIBRE',
    nombre: 'Versión libre (num. 3, art. 112)',
    archivoBase: 'VERSION-LIBRE.docx',
    ruta: `${DIR_PLANTILLAS}/VERSION-LIBRE.docx`,
    marcadores: ['RADICADO', 'INVESTIGADO', 'CARGO_ENTIDAD', 'FECHA_QUEJA', 'CIUDAD', 'FECHA_LETRAS'],
  },
} as const satisfies Record<string, DefinicionPlantilla>;

export type PlantillaId = keyof typeof PLANTILLAS;
